import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static values = {
    src: String,
    alt: String
  };

  connect() {
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    this.boundHandleWheel = this.handleWheel.bind(this);
    this.boundHandleMouseDown = this.handleMouseDown.bind(this);
    this.boundHandleMouseMove = this.handleMouseMove.bind(this);
    this.boundHandleMouseUp = this.handleMouseUp.bind(this);

    this.scale = 1;
    this.rotation = 0;
    this.translateX = 0;
    this.translateY = 0;
    this.isDragging = false;
  }

  open(event) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    let imageSrc = this.hasSrcValue ? this.srcValue : null;

    if (!imageSrc) {
      if (this.element.tagName === "IMG") {
        imageSrc = this.element.src;
      } else {
        const imgTag = this.element.querySelector("img");
        if (imgTag) {
          imageSrc = imgTag.src;
        }
      }
    }

    if (!imageSrc) return;

    const altText = this.hasAltValue ? this.altValue : (this.element.getAttribute("alt") || "Image Preview");

    this.close();

    // Reset transform state
    this.scale = 1;
    this.rotation = 0;
    this.translateX = 0;
    this.translateY = 0;

    const modalDiv = document.createElement("div");
    modalDiv.id = "image-lightbox-modal";
    modalDiv.className = "fixed inset-0 z-[99999] flex flex-col items-center justify-center bg-black/85 backdrop-blur-md p-4 transition-all duration-300 select-none";

    modalDiv.innerHTML = `
      <!-- Toolbar -->
      <div class="fixed top-4 z-30 flex items-center gap-2 bg-gray-900/80 border border-gray-700/60 backdrop-blur-md px-4 py-2 rounded-full shadow-2xl text-white text-sm">
        <button type="button" id="zoom-out-btn" title="Zoom Out (-)" class="p-2 hover:bg-white/20 rounded-full cursor-pointer transition-colors flex items-center justify-center w-8 h-8">
          <i class="fa-solid fa-minus"></i>
        </button>

        <span id="zoom-level-text" class="text-xs font-semibold px-2 min-w-[45px] text-center text-gray-200">100%</span>

        <button type="button" id="zoom-in-btn" title="Zoom In (+)" class="p-2 hover:bg-white/20 rounded-full cursor-pointer transition-colors flex items-center justify-center w-8 h-8">
          <i class="fa-solid fa-plus"></i>
        </button>

        <div class="h-4 w-[1px] bg-gray-700 mx-1"></div>

        <button type="button" id="rotate-btn" title="Rotate 90° (R)" class="p-2 hover:bg-white/20 rounded-full cursor-pointer transition-colors flex items-center justify-center w-8 h-8">
          <i class="fa-solid fa-rotate-right"></i>
        </button>

        <button type="button" id="reset-zoom-btn" title="Reset (0)" class="p-2 hover:bg-white/20 rounded-full cursor-pointer transition-colors flex items-center justify-center w-8 h-8">
          <i class="fa-solid fa-arrow-rotate-left"></i>
        </button>

        <div class="h-4 w-[1px] bg-gray-700 mx-1"></div>

        <button type="button" id="close-image-lightbox" title="Close (Esc)" class="p-2 hover:bg-red-500/80 rounded-full cursor-pointer transition-colors flex items-center justify-center w-8 h-8 text-lg font-bold">
          &times;
        </button>
      </div>

      <!-- Image Display Area -->
      <div id="lightbox-image-container" class="relative w-full h-full flex items-center justify-center overflow-hidden cursor-grab active:cursor-grabbing">
        <img id="lightbox-image" src="${imageSrc}" alt="${altText}" class="max-h-[85vh] max-w-[90vw] object-contain shadow-2xl transition-transform duration-100 ease-out" style="transform: translate(0px, 0px) scale(1) rotate(0deg);" />
      </div>
    `;

    document.body.appendChild(modalDiv);
    document.body.classList.add("overflow-hidden");

    this.lightboxElement = modalDiv;
    this.imgElement = document.getElementById("lightbox-image");
    this.containerElement = document.getElementById("lightbox-image-container");
    this.zoomTextElement = document.getElementById("zoom-level-text");

    // Event listeners for controls
    document.getElementById("zoom-in-btn")?.addEventListener("click", (e) => { e.stopPropagation(); this.zoomIn(); });
    document.getElementById("zoom-out-btn")?.addEventListener("click", (e) => { e.stopPropagation(); this.zoomOut(); });
    document.getElementById("rotate-btn")?.addEventListener("click", (e) => { e.stopPropagation(); this.rotate(); });
    document.getElementById("reset-zoom-btn")?.addEventListener("click", (e) => { e.stopPropagation(); this.resetTransform(); });
    document.getElementById("close-image-lightbox")?.addEventListener("click", (e) => { e.stopPropagation(); this.close(); });

    modalDiv.addEventListener("click", (e) => {
      if (e.target === modalDiv || e.target === this.containerElement) {
        this.close();
      }
    });

    this.containerElement.addEventListener("wheel", this.boundHandleWheel, { passive: false });
    this.imgElement.addEventListener("mousedown", this.boundHandleMouseDown);

    document.addEventListener("mousemove", this.boundHandleMouseMove);
    document.addEventListener("mouseup", this.boundHandleMouseUp);
    document.addEventListener("keydown", this.boundHandleKeyDown);
  }

  zoomIn(step = 0.25) {
    this.scale = Math.min(this.scale + step, 5);
    this.updateTransform();
  }

  zoomOut(step = 0.25) {
    this.scale = Math.max(this.scale - step, 0.25);
    if (this.scale <= 1) {
      this.translateX = 0;
      this.translateY = 0;
    }
    this.updateTransform();
  }

  rotate() {
    this.rotation = (this.rotation + 90) % 360;
    this.updateTransform();
  }

  resetTransform() {
    this.scale = 1;
    this.rotation = 0;
    this.translateX = 0;
    this.translateY = 0;
    this.updateTransform();
  }

  updateTransform() {
    if (!this.imgElement) return;
    this.imgElement.style.transform = `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale}) rotate(${this.rotation}deg)`;
    if (this.zoomTextElement) {
      this.zoomTextElement.textContent = `${Math.round(this.scale * 100)}%`;
    }
  }

  handleWheel(event) {
    event.preventDefault();
    if (event.deltaY < 0) {
      this.zoomIn(0.15);
    } else {
      this.zoomOut(0.15);
    }
  }

  handleMouseDown(event) {
    if (event.button !== 0) return;
    event.preventDefault();
    this.isDragging = true;
    this.startX = event.clientX - this.translateX;
    this.startY = event.clientY - this.translateY;
  }

  handleMouseMove(event) {
    if (!this.isDragging) return;
    event.preventDefault();
    this.translateX = event.clientX - this.startX;
    this.translateY = event.clientY - this.startY;
    this.updateTransform();
  }

  handleMouseUp() {
    this.isDragging = false;
  }

  handleKeyDown(event) {
    if (event.key === "Escape") {
      this.close();
    } else if (event.key === "+" || event.key === "=") {
      this.zoomIn();
    } else if (event.key === "-" || event.key === "_") {
      this.zoomOut();
    } else if (event.key === "r" || event.key === "R") {
      this.rotate();
    } else if (event.key === "0") {
      this.resetTransform();
    }
  }

  close() {
    const existingModal = document.getElementById("image-lightbox-modal");
    if (existingModal) {
      existingModal.remove();
    }
    document.body.classList.remove("overflow-hidden");

    document.removeEventListener("mousemove", this.boundHandleMouseMove);
    document.removeEventListener("mouseup", this.boundHandleMouseUp);
    document.removeEventListener("keydown", this.boundHandleKeyDown);
  }

  disconnect() {
    this.close();
  }
}
